import { FolderTree, Link as LinkIcon, Brain, Zap } from "lucide-react";
import { useT } from "@/i18n/LangProvider";
import "@/styles/chat.css";

const ICONS = [FolderTree, LinkIcon, Brain, Zap];

export default function Geo() {
  const { t } = useT();
  const pillars = t("geo.pillars") || [];
  const chat = t("geo.chat") || {};

  return (
    <section
      id="geo"
      data-testid="geo-section"
      className="section relative overflow-hidden"
      style={{ background: "var(--navy-deep)" }}
    >
      <div
        className="absolute -right-40 top-1/3 w-[600px] h-[600px] pointer-events-none"
        style={{
          background:
            "radial-gradient(circle, rgba(212,174,78,0.18) 0%, transparent 60%)",
        }}
        aria-hidden="true"
      />

      <div className="section-inner relative z-10 grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-20">
        <div>
          <p className="eyebrow fade-up">{t("geo.eyebrow")}</p>
          <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl mt-4 mb-8 leading-[1.1] fade-up">
            {t("geo.title1")}{" "}
            <em className="text-[color:var(--gold-light)]">
              {t("geo.title_em")}
            </em>
          </h2>

          <div className="space-y-5 mb-12 fade-up">
            <p className="font-sans-brand text-[color:var(--muted-white)] leading-relaxed text-lg">
              {t("geo.p1")}
            </p>
            <p className="font-sans-brand text-[color:var(--muted-white)] leading-relaxed text-lg">
              {t("geo.p2")}
            </p>
          </div>

          <div className="space-y-4 fade-up">
            {pillars.map((p, i) => {
              const Icon = ICONS[i] || Brain;
              return (
                <div
                  key={p.title}
                  className="card-navy"
                  data-testid={`geo-pillar-${i}`}
                >
                  <div className="flex items-start gap-4">
                    <Icon
                      className="w-6 h-6 text-[color:var(--gold-light)] mt-1 flex-shrink-0"
                      strokeWidth={1.5}
                    />
                    <div>
                      <h3 className="font-serif text-xl md:text-2xl text-[color:var(--white)] mb-2">
                        {p.title}
                      </h3>
                      <p className="font-sans-brand text-[color:var(--muted-white)] text-sm leading-relaxed">
                        {p.body}
                      </p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          <p
            className="font-mono-brand text-xs text-[color:var(--gold-light)] mt-8 fade-up"
            data-testid="geo-footer-note"
          >
            {t("geo.note")}
          </p>
        </div>

        {/* RIGHT - ChatGPT-style mockup */}
        <div className="fade-up">
          <div className="chat-window" data-testid="geo-terminal">
            <div className="chat-topbar">
              <div className="chat-topbar-left">
                <div className="chat-topbar-title">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                    <path d="M22 12c0-5.523-4.477-10-10-10S2 6.477 2 12c0 1.81.482 3.508 1.323 4.972L2 22l5.184-1.292A9.96 9.96 0 0 0 12 22c5.523 0 10-4.477 10-10z" opacity="0.15" />
                    <path d="M12 4.5a7.5 7.5 0 0 1 7.5 7.5 7.5 7.5 0 0 1-11.7 6.21l-.4-.28-2.74.68.68-2.6-.27-.4A7.46 7.46 0 0 1 4.5 12 7.5 7.5 0 0 1 12 4.5z" />
                  </svg>
                  ChatGPT
                </div>
                <span className="chat-topbar-model">GPT-5</span>
              </div>
              <div className="chat-topbar-dots">
                <i /><i /><i />
              </div>
            </div>

            <div className="chat-body">
              <div className="chat-user">{chat.user}</div>

              <div className="chat-assistant">
                <div className="chat-avatar">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                    <path d="M12 2l1.8 5.4L19.2 9 13.8 10.6 12 16l-1.8-5.4L4.8 9l5.4-1.6L12 2zm6 13l.9 2.7 2.7.9-2.7.9L18 22.2 17.1 19.5 14.4 18.6l2.7-.9L18 15z" />
                  </svg>
                </div>
                <div className="chat-assistant-text">
                  <p>
                    {chat.answer_1}{" "}
                    <span className="biz">{chat.biz}</span>
                    {chat.answer_2}
                  </p>
                  <p>{chat.answer_3}</p>
                  <p>
                    <strong>{chat.contact}</strong>{" "}
                    <span className="domain">kosmedico.zuerich</span>
                  </p>
                </div>
              </div>
            </div>

            <div className="chat-input">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                <path d="M21 15.5l-7.5 7.5a5.5 5.5 0 0 1-7.78-7.78l9.19-9.19a3.67 3.67 0 0 1 5.19 5.19l-9.19 9.19a1.83 1.83 0 0 1-2.6-2.6L15 9" />
              </svg>
              <span>{chat.placeholder}</span>
              <span className="chat-input-send" aria-hidden="true">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4">
                  <path d="M12 19V5M5 12l7-7 7 7" />
                </svg>
              </span>
            </div>

            <div className="chat-without" data-testid="geo-without">
              <div className="chat-without-label">{chat.without_label}</div>
              <div className="chat-without-text">{chat.without_text}</div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
